import { unzipSync } from "fflate";

export const ZIP_PREVIEW_LIMITS = {
  MAX_INPUT_BYTES: 20 * 1024 * 1024,
  MAX_TOTAL_UNCOMPRESSED_BYTES: 40 * 1024 * 1024,
  MAX_LISTED_ENTRIES: 500,
  MAX_TEXT_PREVIEW_BYTES: 256 * 1024,
  /** PDFs under this size may be opened in the PDF artifact panel from ZIP preview. */
  MAX_PDF_PANEL_BYTES: 12 * 1024 * 1024,
};

export function isSafeZipEntryPath(name) {
  if (!name || typeof name !== "string") return false;
  const n = name.replace(/\\/g, "/");
  if (n.startsWith("/") || /^[a-zA-Z]:/.test(n)) return false;
  const segments = n.split("/");
  for (const seg of segments) {
    if (seg === "..") return false;
  }
  return true;
}

function looksLikePdfBytes(u8) {
  return (
    u8 &&
    u8.length >= 5 &&
    u8[0] === 0x25 &&
    u8[1] === 0x50 &&
    u8[2] === 0x44 &&
    u8[3] === 0x46
  );
}

function collectPdfBytesForPanel(files) {
  const out = {};
  const max = ZIP_PREVIEW_LIMITS.MAX_PDF_PANEL_BYTES;
  for (const [path, u8] of Object.entries(files)) {
    if (!isSafeZipEntryPath(path) || path.endsWith("/")) continue;
    if (!path.toLowerCase().endsWith(".pdf")) continue;
    if (!u8?.length || u8.length > max) continue;
    if (!looksLikePdfBytes(u8)) continue;
    out[path] = u8;
  }
  return out;
}

function classifyUtf8Preview(bytes, maxLen = ZIP_PREVIEW_LIMITS.MAX_TEXT_PREVIEW_BYTES) {
  if (!bytes?.length) return { kind: "text", text: "" };
  const truncated = bytes.length > maxLen;
  const slice = truncated ? bytes.slice(0, maxLen) : bytes;
  const decoder = new TextDecoder("utf-8", { fatal: false });
  const text = decoder.decode(slice);
  const replacementCount = (text.match(/\uFFFD/g) || []).length;
  if (replacementCount > Math.min(200, slice.length * 0.03)) {
    return { kind: "binary", size: bytes.length };
  }
  return {
    kind: "text",
    text: truncated
      ? `${text}\n\n… Preview truncated (${bytes.length.toLocaleString()} bytes total)`
      : text,
    size: bytes.length,
  };
}

/**
 * Fully decompresses the ZIP in memory — safe only under MAX_INPUT_BYTES / trusted bundles.
 */
export function parseZipForPreview(bytes) {
  if (!bytes?.length) {
    return { error: "Empty archive." };
  }
  if (bytes.length > ZIP_PREVIEW_LIMITS.MAX_INPUT_BYTES) {
    return { error: "Archive too large to preview safely in the browser." };
  }

  let files;
  try {
    files = unzipSync(bytes);
  } catch {
    return { error: "Could not read this ZIP file." };
  }

  const entries = [];
  let totalUncompressed = 0;

  for (const [path, u8] of Object.entries(files)) {
    if (!isSafeZipEntryPath(path) || path.endsWith("/")) continue;
    totalUncompressed += u8.length;
    if (totalUncompressed > ZIP_PREVIEW_LIMITS.MAX_TOTAL_UNCOMPRESSED_BYTES) {
      return { error: "Archive expands to too much data to preview." };
    }
    const preview = classifyUtf8Preview(u8);
    entries.push({
      path,
      size: u8.length,
      ...preview,
    });
  }

  entries.sort((a, b) => a.path.localeCompare(b.path));
  const pdfBytesByPath = collectPdfBytesForPanel(files);
  return {
    entries: entries.slice(0, ZIP_PREVIEW_LIMITS.MAX_LISTED_ENTRIES),
    truncatedList: entries.length > ZIP_PREVIEW_LIMITS.MAX_LISTED_ENTRIES,
    pdfBytesByPath,
  };
}
